if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.currentVariant = null;
        this.originalMediaOrder = [];
        this.filteredMediaData = [];
        this.isInitialized = false;
      }

      connectedCallback() {
        this.init();
      }


      storeOriginalMediaOrder() {
        // Only store original media order once to prevent overwriting with filtered data
        if (this.originalMediaOrder.length > 0) {
          return;
        }

        const allMediaItems = this.querySelectorAll('.product__media-item');
        const allThumbnailItems = this.querySelectorAll('.product__thumbs-item');
        
        this.originalMediaOrder = [];
        
        allMediaItems.forEach((mediaItem, index) => {
          const mediaId = mediaItem.dataset.mediaId;
          const correspondingThumbnail = allThumbnailItems[index];
          const variantIds = mediaItem.dataset.variantIds || '';
          
          this.originalMediaOrder.push({
            mediaItem: mediaItem,
            thumbnail: correspondingThumbnail,
            mediaId: mediaId,
            variantIds: variantIds,
            originalIndex: index
          });
        });
        
        // Initially, filtered data is the same as original - no filtering on page load
        this.filteredMediaData = [...this.originalMediaOrder];
      }

      getFilteredMediaForVariant(variant) {
        if (!this.filterImagesByVariant || !variant) {
          return this.originalMediaOrder;
        }
        
        const variantId = variant.id.toString();
        const featuredMediaId = variant.featured_media ? variant.featured_media.id.toString() : null;
        
        // Filter media items
        const filteredMedia = this.originalMediaOrder.filter(mediaData => {
          const variantIds = mediaData.variantIds;
          
          // Show if belongs to this variant or is shared (no variant IDs)
          const belongsToVariant = variantIds.includes(variantId);
          const isShared = !variantIds.trim();
          
          return belongsToVariant || isShared;
        });
        
        // Sort to put featured image first
        if (featuredMediaId) {
          filteredMedia.sort((a, b) => {
            const aIsFeatured = a.mediaId === featuredMediaId;
            const bIsFeatured = b.mediaId === featuredMediaId;
            
            if (aIsFeatured && !bIsFeatured) return -1;
            if (!aIsFeatured && bIsFeatured) return 1;
            return 0;
          });
        }
        
        return filteredMedia;
      }

      applyMediaFiltering(variant) {
        this.currentVariant = variant;
        this.filteredMediaData = this.getFilteredMediaForVariant(variant);
        
        // Rebuild DOM with filtered and sorted media
        this.rebuildMediaDOM();
        
        // Update all components that depend on media
        this.updateAllComponents();
      }

      rebuildMediaDOM() {
        const mediaList = this.querySelector('.product__media-list');
        const thumbnailsList = this.querySelector('.thumbnail-list');
        
        if (mediaList) {
          // Clear existing media and remove any Swiper-generated elements
          mediaList.innerHTML = '';
          
          // IMPORTANT: Clone the DOM elements to avoid modifying originalMediaOrder
          // When we move elements in/out of DOM, it affects the original references
          this.filteredMediaData.forEach((mediaData, index) => {
            const mediaItem = mediaData.mediaItem.cloneNode(true);
            
            // Remove Swiper-generated classes but keep the base swiper-slide class
            mediaItem.classList.remove('swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev', 'swiper-slide-duplicate');
            
            // Ensure it has the basic swiper-slide class
            if (!mediaItem.classList.contains('swiper-slide')) {
              mediaItem.classList.add('swiper-slide');
            }
            
            mediaList.appendChild(mediaItem);
          });
        }
        
        if (thumbnailsList) {
          // Clear existing thumbnails
          thumbnailsList.innerHTML = '';
          
          // Add filtered thumbnails in new order (also clone to avoid affecting original)
          this.filteredMediaData.forEach((mediaData, index) => {
            if (mediaData.thumbnail) {
              const thumbnailClone = mediaData.thumbnail.cloneNode(true);
              thumbnailsList.appendChild(thumbnailClone);
            }
          });
        }
      }

      updateAllComponents() {
        // Use setTimeout to ensure DOM has been updated before slider operations
        setTimeout(() => {
          // Refresh element references after DOM rebuild
          this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
          
          // Reinitialize sliders completely for reliable operation after DOM changes
          const shouldReinitialize = this.sliderInstance && this.sliderInstance.slider && this.sliderInstance.slider.initialized;
          
          if (shouldReinitialize) {
            // Destroy both sliders
            if (this.sliderInstance && this.sliderInstance.slider) {
              this.sliderInstance.slider.destroy();
              this.sliderInstance = false;
            }
            
            if (this.thumbsInstance && this.thumbsInstance.slider) {
              this.thumbsInstance.slider.destroy();
              this.thumbsInstance = false;
            }
            
            // Reinitialize based on current media layout
            this.updateMediaLayout();
          } else {
            // Handle case where slider wasn't initialized or needs to be created
            this.updateMediaLayout();
          }
          
          // Reinitialize image zoom with filtered data
          if (this.enableImageZoom && this.lightbox) {
            this.lightbox.destroy();
            this.initImageZoom();
          }
        }, 50);
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.filterImagesByVariant = this.dataset.filterImagesByVariant === 'true';
        
        // Store original media order for filtering BEFORE any other initialization
        // This must happen before sliders are initialized or setActiveMedia is called
        this.storeOriginalMediaOrder();
        
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
        
        // Mark as initialized - now it's safe to filter media
        this.isInitialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false, // Disable loop to prevent counting issues with hidden slides
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction'
          },
          threshold: 2,
          on: {
            slideChange: (swiper) => {
              // Standard slide change handling - no need for custom logic
            }
          }
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }


      initImageZoom() {
        let dataSource = [];
        
        // Use filtered media data instead of all media items
        const mediaItems = this.filteredMediaData.map(data => data.mediaItem);
        
        if (mediaItems.length > 0) {
          mediaItems.forEach((media, index) => {
            // Use sequential index instead of original media index for PhotoSwipe
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: index, // Use sequential index
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: index, // Use sequential index
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: index, // Use sequential index
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.activeIndex !== currIndex) {
              this.sliderInstance.slider.slideTo(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              // Find the index of this media item in the filtered data
              const mediaId = media.dataset.mediaId;
              const index = this.filteredMediaData.findIndex(data => data.mediaId === mediaId);
              if (index >= 0) {
                this.lightbox.loadAndOpen(index);
              }
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        // Only apply filtering if component is fully initialized
        if (this.filterImagesByVariant && this.isInitialized) {
          this.applyMediaFiltering(variant);
        } else {
          // For non-filtering mode or during initialization, just navigate to the featured media
          if (this.sliderInstance && this.sliderInstance.slider) {
            const slideIndex = variant.featured_media.position || 0;
            this.sliderInstance.slider.slideToLoop(slideIndex - 1);
          } else {
            // Grid layout - sort media items to show featured image first
            this.sortMediaItems(variant);
          }
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;
        const thumbnailsList = this.querySelector('.thumbnail-list');

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        // Find and move the featured media to the front
        const featuredIndex = newMedias.findIndex(media => media.dataset.mediaId == variant.featured_media.id);
        if (featuredIndex > 0) {
          const [featuredElement] = newMedias.splice(featuredIndex, 1);
          newMedias.unshift(featuredElement);
        }

        // Update main media list
        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Update thumbnails to match the new order
        if (thumbnailsList) {
          const allThumbnails = Array.from(thumbnailsList.children);
          
          // Reset thumbnail ordering
          allThumbnails.sort(function (a, b) {
            return a.dataset.mediaPosition - b.dataset.mediaPosition;
          });

          // Move featured thumbnail to front
          const featuredThumbnailIndex = allThumbnails.findIndex(thumb => thumb.dataset.target == variant.featured_media.id);
          if (featuredThumbnailIndex > 0) {
            const [featuredThumbnail] = allThumbnails.splice(featuredThumbnailIndex, 1);
            allThumbnails.unshift(featuredThumbnail);
          }

          // Rebuild thumbnail list
          thumbnailsList.innerHTML = '';
          allThumbnails.forEach((thumbnail) => {
            thumbnailsList.appendChild(thumbnail);
          });
        }

        // Update slider to show the featured image (now at index 0) after DOM reordering
        if (this.sliderInstance && this.sliderInstance.slider) {
          // Use setTimeout to ensure DOM changes are complete before slider update
          setTimeout(() => {
            this.sliderInstance.slider.update();
            this.sliderInstance.slider.slideTo(0, 0); // Featured image is now at index 0
          }, 50);
        }

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }


    }
  );
}
